import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, Loader2, Target } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const AcquisitionIntelPage = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const analyze = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/intelligence/acquisition/analyze`, {
        firm_id: "demo_firm",
        analysis_period_months: 12
      });
      setReport(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    analyze();
  }, []);
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="acquisition-intel-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-7xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Case Acquisition Intelligence</h1>
            <Badge className="bg-gradient-to-r from-green-600 to-emerald-600">PREDICTIVE MARKETING</Badge>
          </div>
          <p className="text-muted-foreground">Optimize your marketing spend, find better cases, increase ROI</p>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : report && (
          <div className="space-y-6" data-testid="acquisition-results">
            {/* Revenue Impact Summary */}
            <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
              <CardContent className="p-6">
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <p className="text-emerald-100 text-sm mb-1">Current Monthly Revenue</p>
                    <p className="text-3xl font-bold">${Math.round(report.current_monthly_revenue).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-emerald-100 text-sm mb-1">Projected Monthly Revenue</p>
                    <p className="text-3xl font-bold">${Math.round(report.projected_monthly_revenue).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-emerald-100 text-sm mb-1">Improvement Potential</p>
                    <p className="text-3xl font-bold">+{report.improvement_percentage}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Lead Source Analysis */}
            <Card>
              <CardHeader>
                <CardTitle>Lead Source Performance</CardTitle>
                <CardDescription>Which sources deliver the best cases?</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {report.lead_sources?.map((source, i) => (
                    <div key={i} className="p-4 rounded-lg border">
                      <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${i === 0 ? 'bg-emerald-500' : i === report.lead_sources.length - 1 ? 'bg-red-500' : 'bg-blue-500'}`} />
                          <span className="font-semibold">{source.source_name}</span>
                          <Badge variant="outline">{source.source_category}</Badge>
                        </div>
                        <div className="text-right">
                          <span className="text-2xl font-bold text-emerald-600">{source.roi}x</span>
                          <span className="text-sm text-muted-foreground ml-1">ROI</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-5 gap-4 text-sm">
                        <div><span className="text-muted-foreground">Leads:</span> {source.total_leads}</div>
                        <div><span className="text-muted-foreground">Signed:</span> {source.signed_cases}</div>
                        <div><span className="text-muted-foreground">Conv:</span> {(source.conversion_rate * 100).toFixed(0)}%</div>
                        <div><span className="text-muted-foreground">Avg Value:</span> ${source.average_case_value.toLocaleString()}</div>
                        <div><span className="text-muted-foreground">CPA:</span> ${source.cost_per_acquisition.toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Recommendations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-500" />
                  Actionable Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {report.recommendations?.map((rec, i) => (
                    <div key={i} className="p-4 rounded-lg border bg-blue-50 border-blue-200">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-semibold">{rec.title}</h4>
                        <div className="flex gap-2">
                          <Badge variant={rec.priority === 'high' ? 'destructive' : 'secondary'}>{rec.priority}</Badge>
                          <Badge variant="outline">{rec.effort} effort</Badge>
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{rec.description}</p>
                      <p className="text-emerald-700 font-semibold">{rec.potential_impact}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Carrier Insights */}
            <Card>
              <CardHeader>
                <CardTitle>Insurance Carrier Insights</CardTitle>
                <CardDescription>How carriers behave in negotiations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {report.carrier_insights?.map((carrier, i) => (
                    <div key={i} className="p-4 rounded-lg border">
                      <h4 className="font-semibold mb-2">{carrier.carrier_name}</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                        <div><span className="text-muted-foreground">Avg Settlement:</span> ${carrier.average_settlement.toLocaleString()}</div>
                        <div><span className="text-muted-foreground">Avg Days:</span> {carrier.average_days_to_settlement}</div>
                        <div><span className="text-muted-foreground">First Offer:</span> {(carrier.first_offer_percentage * 100).toFixed(0)}% of final</div>
                      </div>
                      <p className="text-sm p-2 rounded bg-slate-100"><strong>Strategy:</strong> {carrier.negotiation_strategy}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default AcquisitionIntelPage;
